import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  OperatorApproval,
  OperatorApprovalAction,
  OperatorAuthFailure,
  OperatorSignIn
} from 'src/app/models';
import { BaseService } from 'src/app/services/base.service';

interface DataEnvelope<T> {
  message: string;
  data: T;
}

const KNOWN_FAILURES: readonly OperatorAuthFailure[] = [
  'invalid_pin',
  'too_many_attempts',
  'pin_reset_required',
  'insufficient_permissions',
  'operator_authentication_required'
];

/**
 * POS-1927: server-side PIN sign-in and manager approvals, used when the
 * facility has `pos_server_side_operator_auth` on.
 *
 * These calls deliberately skip `ErrorService.retryPipe`: it treats every 401
 * as an expired device session and resets the app, and here a 401 only means
 * "wrong PIN".
 */
@Injectable({
  providedIn: 'root'
})
export class OperatorAuthService extends BaseService {
  // The latest approval per action, waiting for the void/refund request that
  // uses it. The backend burns a token on first use, so it is handed out once.
  private readonly approvals = new Map<
    OperatorApprovalAction,
    OperatorApproval
  >();

  constructor(store: Store, private readonly http: HttpClient) {
    super(store);
  }

  static failureOf(error: unknown): OperatorAuthFailure {
    const status =
      error instanceof HttpErrorResponse ? error.error?.status : undefined;
    return KNOWN_FAILURES.includes(status) ? status : 'unknown';
  }

  signIn(operatorId: number, pin_code: string): Observable<OperatorSignIn> {
    return this.http
      .post<DataEnvelope<OperatorSignIn>>(
        this.getLocationUrl(`operators/${operatorId}/sign_in`),
        { pin_code }
      )
      .pipe(map((response) => response.data));
  }

  /**
   * Without a PIN, the backend accepts the signed-in operator's own session
   * (X-Pos-Operator-Session) as proof, for approving their own action.
   */
  requestApproval(
    operatorId: number,
    approval_action: OperatorApprovalAction,
    pin_code?: string
  ): Observable<OperatorApproval> {
    const body = pin_code ? { approval_action, pin_code } : { approval_action };

    return this.http
      .post<DataEnvelope<OperatorApproval>>(
        this.getLocationUrl(`operators/${operatorId}/approval`),
        body
      )
      .pipe(
        map((response) => response.data),
        tap((approval) =>
          this.approvals.set(approval.approval_action, approval)
        )
      );
  }

  /** The approval token for this approver, if one is waiting and unexpired. */
  takeApprovalToken(
    action: OperatorApprovalAction,
    approvingOperatorId: number | null | undefined
  ): string | undefined {
    const approval = this.approvals.get(action);
    if (!approval || approval.approving_operator_id !== approvingOperatorId) {
      return undefined;
    }

    this.approvals.delete(action);
    if (Date.parse(approval.expires_at) <= Date.now()) {
      return undefined;
    }

    return approval.approval_token;
  }
}
